import { useCallback, useState } from "react";
import { generateRctiInvoiceService as defaultService } from "../services/generateRctiInvoiceService";

// ─── Hook ─────────────────────────────────────────────────────────────────────

const INITIAL_STATE = {
  status: "idle",
  generatingPlaceId: null,
  invoiceUrl: null,
  error: null,
};

/**
 * One-shot mutation hook for generate_rcti_invoice.
 *
 * No race condition guard needed — this is a single sequential mutation with
 * no concurrent call scenario (ADR-004). Tracks which placeId is being
 * generated so the UI can show per-row loading state.
 *
 * `service` is injected for testability.
 */
export function useGenerateRctiInvoice(service = defaultService) {
  const [state, setState] = useState(INITIAL_STATE);

  const generate = useCallback(
    async ({ placeId, fromDate, toDate }) => {
      setState({
        status: "loading",
        generatingPlaceId: placeId,
        invoiceUrl: null,
        error: null,
      });
      try {
        const result = await service.generateRctiInvoice({
          placeId,
          fromDate,
          toDate,
        });
        setState({
          status: "success",
          generatingPlaceId: placeId,
          invoiceUrl: result.invoiceUrl,
          error: null,
        });
        return result;
      } catch (err) {
        setState({
          status: "error",
          generatingPlaceId: placeId,
          invoiceUrl: null,
          error: err,
        });
        return null;
      }
    },
    [service],
  );

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return {
    rctiStatus: state.status,
    generatingPlaceId: state.generatingPlaceId,
    rctiUrl: state.invoiceUrl,
    rctiError: state.error,
    generate,
    reset,
  };
}
